import {
    type ClipboardEvent,
    type KeyboardEvent,
    type MouseEvent,
    useCallback,
    useMemo,
} from "react";
import { flushSync } from "react-dom";
import type { RowData } from "@tanstack/react-table";
import { isInsideInteractiveCellContent } from "../cells/cellControls";
import { isEditableTarget } from "../focus/editableTarget";
import type { GridController } from "../focus/gridController";
import { IS_APPLE_PLATFORM, normalizeKey } from "../focus/keyMap";
import {
    getCellAddressFromTarget,
    getFocusedAddress,
} from "../focus/navigation";
import type { DataGridInstance } from "../types";
import { getCell, getEditableCell } from "./cellAccess";
import { copyRange, cutRange, pasteText } from "./clipboardActions";
import {
    clearRange,
    type EditContext,
    replayHistory,
    startEdit,
} from "./editActions";

/**
 * A key that types a character: Option (macOS) and AltGr (Ctrl+Alt on
 * Windows) characters count, other Ctrl, Alt, or Meta presses are shortcuts.
 */
const isTypedCharacter = (event: KeyboardEvent<HTMLDivElement>) => {
    const isAltGraph = event.getModifierState("AltGraph");
    return (
        event.key.length === 1 &&
        event.key !== " " &&
        !event.metaKey &&
        (!event.ctrlKey || isAltGraph) &&
        (!event.altKey || IS_APPLE_PLATFORM || isAltGraph)
    );
};

/** An IME or dead-key press: the composed character arrives later, in whatever input has focus. */
const isCompositionKey = (event: KeyboardEvent<HTMLDivElement>) =>
    event.key === "Process" ||
    event.key === "Dead" ||
    event.nativeEvent.isComposing;

/**
 * Excel keys on the active cell: type to replace, Enter or F2 to edit in
 * place, Delete or Backspace to clear the range, Mod+Z / Mod+Shift+Z / Mod+Y
 * for undo and redo. Returns whether the key was handled. An IME or dead key
 * opens the editor empty but counts as unhandled, so the browser's default
 * carries the composition into the editor's input.
 */
const handleEditingKey = <TData extends RowData>(
    context: EditContext<TData>,
    event: KeyboardEvent<HTMLDivElement>,
): boolean => {
    const { instance } = context;
    const pressed = normalizeKey(event);
    if (pressed.isMod && !pressed.isAlt && !pressed.hasOtherModifier) {
        if (pressed.key === "z") {
            return replayHistory(context, pressed.isShift ? "redo" : "undo");
        }
        return pressed.key === "y" && replayHistory(context, "redo");
    }
    const address = getFocusedAddress(instance.table.atoms.cellSelection.get());
    if (!address) {
        return false;
    }
    if (isCompositionKey(event)) {
        // The editor has to hold focus before the key's default runs, or the
        // browser starts the composition on the cell and the accent is lost.
        flushSync(() => startEdit(context, { address, seed: "" }));
        return false;
    }
    if (isTypedCharacter(event)) {
        return startEdit(context, { address, seed: event.key });
    }
    if (pressed.isAlt || pressed.hasOtherModifier) {
        return false;
    }
    switch (pressed.key) {
        case "f2":
            return startEdit(context, { address, seed: null });
        case "enter":
            return (
                !pressed.isShift && startEdit(context, { address, seed: null })
            );
        case "delete":
        case "backspace":
            return clearRange(context);
        default:
            return false;
    }
};

interface UseEditingHandlersInput<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
}

/** Spreadsheet handlers for the grid element: keys, clipboard, double-click. */
export const useEditingHandlers = <TData extends RowData>({
    instance,
    controller,
}: UseEditingHandlersInput<TData>) => {
    const context = useMemo(
        () => ({ instance, controller }),
        [instance, controller],
    );
    const isCellMode = instance.hasCellNavigation;
    const { isEditable } = instance;

    const onCellKey = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) =>
            isEditable && handleEditingKey(context, event),
        [context, isEditable],
    );

    const writeClipboard = useCallback(
        (
            event: ClipboardEvent<HTMLDivElement>,
            copied: ReturnType<typeof copyRange>,
        ) => {
            if (!copied) {
                return;
            }
            event.preventDefault();
            event.clipboardData.setData("text/plain", copied.tsv);
            event.clipboardData.setData("text/html", copied.html);
        },
        [],
    );

    const onCopy = useCallback(
        (event: ClipboardEvent<HTMLDivElement>) => {
            if (isCellMode && !isEditableTarget(event.target)) {
                writeClipboard(event, copyRange(context));
            }
        },
        [context, isCellMode, writeClipboard],
    );
    const onCut = useCallback(
        (event: ClipboardEvent<HTMLDivElement>) => {
            if (isEditable && !isEditableTarget(event.target)) {
                writeClipboard(event, cutRange(context));
            }
        },
        [context, isEditable, writeClipboard],
    );
    const onPaste = useCallback(
        (event: ClipboardEvent<HTMLDivElement>) => {
            if (!isEditable || isEditableTarget(event.target)) {
                return;
            }
            const text = event.clipboardData.getData("text/plain");
            // A paste with no text (an image, a file) reads as "" and must not clear cells; a copied blank cell is "\n".
            if (text !== "" && pasteText(context, text)) {
                event.preventDefault();
            }
        },
        [context, isEditable],
    );

    const onDoubleClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            const address = isCellMode
                ? getCellAddressFromTarget(event.target)
                : null;
            if (
                !address ||
                isEditableTarget(event.target) ||
                isInsideInteractiveCellContent(
                    event.target,
                    event.currentTarget,
                )
            ) {
                return;
            }
            const cell = getCell(instance.table, address);
            if (getEditableCell(instance, cell)) {
                startEdit(context, { address, seed: null });
                return;
            }
            const { onRowOpen } = instance.optionsRef.current;
            if (cell && onRowOpen && instance.hasRangeSelection) {
                onRowOpen(cell.row, address.columnId);
            }
        },
        [context, instance, isCellMode],
    );

    return { onCellKey, onCopy, onCut, onPaste, onDoubleClick };
};
